import { ReactFlowProvider } from '@xyflow/react';
import { CopyIcon, PlayIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { DuplicateQueryModal } from '@/sections/insights/queryBuilder/components/Modals';
import { QueryVisualizer } from '@/sections/insights/queryBuilder/components/QueryVisualizer';
import { useQueryOperations } from '@/sections/insights/queryBuilder/hooks/useQueryOperations';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';

import { UserSelectorDropdown } from './UserSelectorDropdown';

/**
 * QueryViewer - Read-only query inspection interface
 *
 * Displays query structure with:
 * - Action bar with Execute and Duplicate buttons
 * - Visual graph showing query structure (QueryVisualizer component)
 * - Clickable nodes/edges that open read-only detail modals
 *
 * Triggered by:
 * - QueryMenu "View Query" action (all queries)
 * - QueryBuilderWorkstation when mode === 'view'
 *
 * Header is rendered by parent QueryBuilderWorkstation
 */
export const QueryViewer = () => {
  const { queryState, isGlobal, getSavedQuery } = useQueryBuilder();
  const { handleExecute } = useQueryOperations();
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  if (!queryState) {
    toast.error('No query selected');
    return null;
  }

  const handleDuplicate = () => {
    setIsDuplicateModalOpen(true);
  };

  return (
    <>
      <div className="relative flex h-full flex-col">
        {/* Floating action panel - top right */}
        <div
          className="border-chariot-stroke bg-chariot-background-primary absolute top-4 right-4 z-10 flex items-center gap-2 rounded-md border px-3 py-2"
          data-testid="query-viewer-actions-panel"
          role="group"
          aria-label="Query actions"
        >
          {/* Execute - Primary action */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExecute}
            className="rounded-full bg-chariot-brand-primary hover:bg-chariot-brand-secondary text-white"
            data-testid="query-viewer-execute-btn"
            aria-label="Execute query"
          ><PlayIcon className="size-4" />Execute</Button>

          {/* Separator */}
          <div
            className="bg-chariot-stroke h-6 w-px"
            role="separator"
            aria-orientation="vertical"
          />

          {/* NEW: Users dropdown (conditional) - Component handles everything internally */}
          {isGlobal && (
            <>
              <UserSelectorDropdown />
              <div
                className="bg-chariot-stroke h-6 w-px"
                role="separator"
                aria-orientation="vertical"
              />
            </>
          )}

          {/* Duplicate - Secondary action */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDuplicate}
            className="rounded-full"
            data-testid="query-viewer-duplicate-btn"
            aria-label="Duplicate query"
          ><CopyIcon className="size-4" />Duplicate</Button>
        </div>

        <ReactFlowProvider>
          <QueryVisualizer editable={false} />
        </ReactFlowProvider>
      </div>

      {getSavedQuery() && (
        <DuplicateQueryModal
          isOpen={isDuplicateModalOpen}
          onClose={() => setIsDuplicateModalOpen(false)}
          originalQuery={getSavedQuery()!}
        />
      )}
    </>
  );
};
